// Pulse keyframes used by badges, dots, and primary button.
// Mount once near the app root (e.g. in App.js) so every page gets them.

import React from "react";

export const THEME_VARS = `
  /* ── Cyber theme (deep-navy glassmorphism, neon blue/purple) ──
     Variable NAMES are unchanged so every page re-skins automatically;
     only the values changed. --t-page stays a SOLID color (it is used as
     an SVG/canvas fill in Infrastructure, which cannot take a gradient).
     The radial neon glow lives in --t-page-glow, layered by body + Layout. */
  :root, [data-theme="dark"] {
    --badge-blue:    #93c5fd;
    --badge-amber:   #fde68a;
    --badge-orange:  #fb923c;
    --badge-green:   #6ee7b7;
    --badge-red:     #fca5a5;
    --badge-pink:    #fcd34d;
    --badge-gray:    #94a3b8;
    --badge-violet:  #c4b5fd;
    --t-page:         #050b1a;
    --t-page-glow:    radial-gradient(circle at 12% -5%, rgba(79,111,255,0.22), transparent 40%), radial-gradient(circle at 88% 8%, rgba(123,97,255,0.12), transparent 38%);
    --t-panel:        #0d1a36;
    --t-panel-hi:     #14264a;
    --t-input:        #122340;
    --t-input-hi:     #1a2e52;
    --t-card-input:   #173158;
    --t-card-head:    #101f3e;
    --t-surface-mid:  #1f3457;
    --t-surface-deep: #0a1530;
    --t-border-soft:   rgba(96,124,255,0.26);
    --t-border-softer: rgba(96,124,255,0.15);
    --t-border-mid:    rgba(96,124,255,0.38);
    --t-border-strong: rgba(96,124,255,0.55);
    --t-text-primary: #f4f8ff;
    --t-text-body:    #cdd9ef;
    --t-text-muted:   #93a6c9;
    --t-text-label:   #7188ad;
    --t-text-faint:   #5d6f93;
    --t-text-deep:    #46587a;
    --t-shadow-panel:    0 8px 26px rgba(0,0,0,0.45), 0 0 0 1px rgba(96,124,255,0.05), 0 0 18px rgba(79,111,255,0.07), inset 0 1px 0 rgba(255,255,255,0.05);
    --t-shadow-panel-hi: 0 12px 34px rgba(0,0,0,0.5), 0 0 26px rgba(79,111,255,0.16), inset 0 1px 0 rgba(255,255,255,0.06);
    --t-grad-sidebar:    linear-gradient(180deg,#0a1326 0%,#070e1d 100%);
    --t-grad-panel-head: linear-gradient(180deg,rgba(22,38,69,0.9),rgba(16,29,56,0.65));
    --t-grad-list-head:  linear-gradient(180deg,rgba(16,29,56,0.9),rgba(16,29,56,0.55));
    --t-compose-bg:      rgba(16,29,56,0.96);
    --t-note-bg:         rgba(30,16,0,0.9);
    --t-note-bdr:        #78350f;
    /* Accent — single source of truth. tokens.js routes the primary button/
       glow/selection through these, so a per-user brand theme (below) re-skins
       the whole app by overriding just these. Default = original Cyber. */
    --t-accent:        #4f6fff;
    --t-accent-2:      #7b61ff;
    --t-accent-grad:   linear-gradient(135deg,#4f6fff,#7b61ff);
    --t-accent-glow:   0 4px 18px rgba(79,111,255,0.45);
    --t-accent-soft:   rgba(79,111,255,0.22);
    --t-accent-softer: rgba(79,111,255,0.07);
    /* Corner radius is themed too — routed through tokens.radius so a brand
       can feel soft (Cyber/Clean) or sharp/technical (Bold/Terminal). */
    --t-radius-sm:    8px;
    --t-radius-md:    12px;
    --t-radius-lg:    16px;
    /* Typography + button personality — each brand gets its own voice.
       --t-font      = UI/body face (inherited by every component)
       --t-font-head = h1-h4 ONLY, so a display face cannot widen dense tables
       --t-font-btn  = buttons, which carry a lot of a theme's character
       CYBER = futuristic / scientific: geometric technical body, Orbitron
       display, and wide-tracked uppercase buttons for a HUD feel. */
    --t-font:          'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    --t-font-head:     'Orbitron', 'Space Grotesk', -apple-system, sans-serif;
    --t-font-btn:      'Orbitron', 'Space Grotesk', -apple-system, sans-serif;
    --t-btn-weight:    700;
    --t-btn-transform: uppercase;
    --t-btn-letter:    0.10em;
    --t-btn-shadow:    var(--t-accent-glow);
    /* ── Left rail ── the sidebar is a themed surface with its OWN text ramp,
       so it re-skins with the brand. Layout.js reads ONLY these + --t-accent*
       for the rail now — no more hardcoded Cyber blue (pill/avatar/toggle). */
    --t-rail-bg:          var(--t-grad-sidebar);
    --t-rail-text:        #93a6c9;
    --t-rail-text-strong: var(--t-text-primary);
    --t-rail-dim:         var(--t-text-faint);
    --t-rail-border:      var(--t-border-softer);
    --t-rail-card-bg:     rgba(18,30,51,0.9);
    /* ── Icon personality ── stroke weight / cap / glow per brand, applied to
       lucide icons via svg.lucide in index.css (CSS beats the presentation
       attrs), so the whole icon set switches with the theme. */
    --t-ico-sw:    1.8;
    --t-ico-cap:   round;
    --t-ico-glow:  drop-shadow(0 0 3px rgba(79,111,255,0.45));
    /* ── Surface finish (2026-10-23, for Glossy + Neon) ── off by default.
       --t-gloss       = a highlight layered OVER a surface: background: var(--t-gloss), var(--t-panel)
       --t-backdrop    = backdrop-filter for floating chrome (rail overlay, sticky bars)
       --t-head-shadow = glow on h1-h4 (index.css) */
    --t-gloss:       none;
    --t-backdrop:    none;
    --t-head-shadow: none;
  }
  [data-theme="light"] {
    --badge-blue:    #1d4ed8;
    --badge-amber:   #92400e;
    --badge-orange:  #9a3412;
    --badge-green:   #065f46;
    --badge-red:     #991b1b;
    --badge-pink:    #92400e;
    --badge-gray:    #475569;
    --badge-violet:  #5b21b6;
    --t-page:         #f4f7fc;
    --t-page-glow:    radial-gradient(circle at 12% -5%, rgba(79,111,255,0.07), transparent 44%);
    --t-panel:        #ffffff;
    --t-panel-hi:     #f7f9fe;
    --t-input:        #ffffff;
    --t-input-hi:     #eef3fb;
    --t-card-input:   #eef4ff;
    --t-card-head:    #eef3fb;
    --t-surface-mid:  #e6edf8;
    --t-surface-deep: #dae3f1;
    --t-border-soft:   rgba(60,90,200,0.18);
    --t-border-softer: rgba(60,90,200,0.10);
    --t-border-mid:    rgba(60,90,200,0.30);
    --t-border-strong: rgba(60,90,200,0.48);
    --t-text-primary: #0b1428;
    --t-text-body:    #1e293b;
    --t-text-muted:   #475569;
    --t-text-label:   #5a6b8c;
    --t-text-faint:   #7a8fa5;
    --t-text-deep:    #9aafc4;
    --t-shadow-panel:    0 2px 12px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.9);
    --t-shadow-panel-hi: 0 4px 20px rgba(0,0,0,0.09), 0 2px 6px rgba(0,0,0,0.07), inset 0 1px 0 rgba(255,255,255,0.9);
    --t-grad-sidebar:    linear-gradient(180deg,#0b1c34 0%,#081428 100%);
    --t-grad-panel-head: linear-gradient(180deg,rgba(255,255,255,1),rgba(247,249,252,0.95));
    --t-grad-list-head:  linear-gradient(180deg,rgba(240,244,248,1),rgba(240,244,248,0.8));
    --t-compose-bg:      #f7f9fc;
    --t-note-bg:         rgba(251,191,36,0.06);
    --t-note-bdr:        rgba(180,83,9,0.4);
    /* Cyber keeps a dark rail even in light mode (its neon identity); only the
       text ramp is set light-on-dark here. --t-ico-* inherit from :root. */
    --t-rail-bg:          var(--t-grad-sidebar);
    --t-rail-text:        #a9b8d4;
    --t-rail-text-strong: #f4f8ff;
    --t-rail-dim:         #7188ad;
    --t-rail-border:      rgba(255,255,255,0.08);
    --t-rail-card-bg:     rgba(255,255,255,0.06);
  }

  /* ── Per-user brand themes ──────────────────────────────────────────────
     Each brand owns its FULL palette — surfaces, text and shadows, not just
     the accent. (Accent-only overrides made every theme look near-identical
     in the same mode, which is the "small outline colour changes" complaint.)

     SELECTOR STRATEGY — this matters:
       * A mode-agnostic [data-brand-theme="x"] block holds only things that
         are true in both modes: the accent ramp and the corner radius.
       * Surfaces live in COMPOUND [data-theme="…"][data-brand-theme="…"]
         blocks (specificity 0,2,0). They must be compound: a bare
         [data-brand-theme] block is 0,1,0 — the same as [data-theme="light"]
         — and being later in the source it would beat it, so a light-mode
         user on any brand would get dark surfaces.
       * Anything a brand does not set falls through to the base blocks above
         (badge colours, notes, etc.), which is intentional.
       * "cyber" needs no block at all — it IS the base palette.
     ThemeContext always writes both attributes, so these always resolve. */

  /* ══ CLEAN — professional, neutral slate, crisp edges, restrained glow ══ */
  [data-brand-theme="clean"] {
    --t-accent:        #2563eb;
    --t-accent-2:      #3b82f6;
    --t-accent-grad:   linear-gradient(135deg,#2563eb,#3b82f6);
    --t-accent-glow:   0 3px 12px rgba(37,99,235,0.30);
    --t-accent-soft:   rgba(37,99,235,0.16);
    --t-accent-softer: rgba(37,99,235,0.06);
    --t-radius-sm:     6px;
    --t-radius-md:     10px;
    --t-radius-lg:     14px;
    /* CLEAN = corporate/professional: neutral Inter throughout, sentence-case
       flat buttons. The restraint is the personality here. */
    --t-font:          'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
    --t-font-head:     'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
    --t-font-btn:      'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
    --t-btn-weight:    600;
    --t-btn-transform: none;
    --t-btn-letter:    0;
    --t-btn-shadow:    0 1px 2px rgba(15,23,42,0.16);
    /* Clean icons: neutral medium stroke, no glow — flat like the rest of it. */
    --t-ico-sw:    1.9;
    --t-ico-cap:   round;
    --t-ico-glow:  none;
  }
  [data-theme="dark"][data-brand-theme="clean"] {
    --t-rail-bg:          var(--t-grad-sidebar);
    --t-rail-text:        #94a3b8;
    --t-rail-text-strong: #f1f5f9;
    --t-rail-dim:         #64748b;
    --t-rail-border:      rgba(148,163,184,0.10);
    --t-rail-card-bg:     rgba(148,163,184,0.06);
    --t-page:         #0f141b;
    /* No glow at all: "professional" reads flat and neutral, which is also
       what separates it from Cyber's glassmorphism at a glance. */
    --t-page-glow:    none;
    --t-panel:        #171e28;
    --t-panel-hi:     #1f2833;
    --t-input:        #1b232e;
    --t-input-hi:     #242e3a;
    --t-card-input:   #202a36;
    --t-card-head:    #1a222c;
    --t-surface-mid:  #2a3542;
    --t-surface-deep: #0c1117;
    --t-border-soft:   rgba(148,163,184,0.18);
    --t-border-softer: rgba(148,163,184,0.10);
    --t-border-mid:    rgba(148,163,184,0.28);
    --t-border-strong: rgba(148,163,184,0.42);
    --t-text-primary: #f1f5f9;
    --t-text-body:    #cbd5e1;
    --t-text-muted:   #94a3b8;
    --t-text-label:   #7c8b9d;
    --t-text-faint:   #64748b;
    --t-text-deep:    #4b5768;
    --t-shadow-panel:    0 4px 16px rgba(0,0,0,0.35), 0 0 0 1px rgba(148,163,184,0.06);
    --t-shadow-panel-hi: 0 8px 24px rgba(0,0,0,0.42), 0 0 0 1px rgba(148,163,184,0.09);
    --t-grad-sidebar:    linear-gradient(180deg,#131a23 0%,#0e141b 100%);
    --t-grad-panel-head: linear-gradient(180deg,rgba(31,40,51,0.9),rgba(23,30,40,0.65));
    --t-grad-list-head:  linear-gradient(180deg,rgba(26,34,44,0.92),rgba(26,34,44,0.6));
    --t-compose-bg:      rgba(23,30,40,0.96);
  }
  [data-theme="light"][data-brand-theme="clean"] {
    /* Deliberately a NEUTRAL grey page, not the blue-tinted near-white Cyber
       light uses — otherwise the two light themes are indistinguishable.
       Flat (no glow) + crisper borders is what makes this one read as Clean. */
    --t-page:         #eef1f5;
    --t-page-glow:    none;
    --t-panel:        #ffffff;
    --t-panel-hi:     #fafbfc;
    --t-input:        #ffffff;
    --t-input-hi:     #eef1f5;
    --t-card-input:   #f6f7f9;
    --t-card-head:    #eef1f5;
    --t-surface-mid:  #dfe4ea;
    --t-surface-deep: #c6cdd6;
    --t-border-soft:   rgba(100,116,139,0.26);
    --t-border-softer: rgba(100,116,139,0.15);
    --t-border-mid:    rgba(100,116,139,0.36);
    --t-border-strong: rgba(100,116,139,0.52);
    --t-text-primary: #0f172a;
    --t-text-body:    #1e293b;
    --t-text-muted:   #475569;
    --t-text-label:   #64748b;
    --t-text-faint:   #94a3b8;
    --t-text-deep:    #b8c4d4;
    --t-shadow-panel:    0 1px 3px rgba(15,23,42,0.08), 0 1px 2px rgba(15,23,42,0.04);
    --t-shadow-panel-hi: 0 4px 12px rgba(15,23,42,0.10), 0 2px 4px rgba(15,23,42,0.06);
    /* Sidebar stays dark in light mode (as Cyber light already does) — the
       sidebar's nav text is light, so a pale sidebar would be unreadable. */
    --t-grad-sidebar:    linear-gradient(180deg,#232a33 0%,#1a2029 100%);
    --t-grad-panel-head: linear-gradient(180deg,#ffffff,#f6f7f9);
    --t-grad-list-head:  linear-gradient(180deg,#eef1f5,rgba(238,241,245,0.75));
    --t-compose-bg:      #f6f7f9;
    /* CLEAN's signature move: a genuinely LIGHT rail (white, dark text) in
       light mode — the clearest sign the rail follows the theme now. */
    --t-rail-bg:          #ffffff;
    --t-rail-text:        #475569;
    --t-rail-text-strong: #0f172a;
    --t-rail-dim:         #94a3b8;
    --t-rail-border:      rgba(100,116,139,0.16);
    --t-rail-card-bg:     #f1f5f9;
  }

  /* ══ BOLD — editorial, warm cream, high contrast, sharp corners ══ */
  [data-brand-theme="bold"] {
    --t-accent:        #e8431a;
    --t-accent-2:      #ff6a2e;
    --t-accent-grad:   linear-gradient(135deg,#e8431a,#ff6a2e);
    --t-accent-glow:   0 3px 14px rgba(232,67,26,0.38);
    --t-accent-soft:   rgba(232,67,26,0.16);
    --t-accent-softer: rgba(232,67,26,0.06);
    --t-radius-sm:     3px;
    --t-radius-md:     5px;
    --t-radius-lg:     8px;
    /* BOLD = editorial/magazine: Playfair Display headlines over Inter body
       (serif HEADINGS only, so tables and dense lists keep their measure),
       and heavy uppercase buttons with a hard print-style offset. */
    --t-font:          'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    --t-font-head:     'Playfair Display', Georgia, 'Times New Roman', serif;
    --t-font-btn:      'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    --t-btn-weight:    800;
    --t-btn-transform: uppercase;
    --t-btn-letter:    0.06em;
    --t-btn-shadow:    0 2px 0 rgba(90,60,25,0.45);
    /* Bold icons: heavier stroke, square caps + sharp joins — editorial weight. */
    --t-ico-sw:    2.25;
    --t-ico-cap:   square;
    --t-ico-glow:  none;
  }
  [data-theme="dark"][data-brand-theme="bold"] {
    --t-rail-bg:          var(--t-grad-sidebar);
    --t-rail-text:        #b9a68c;
    --t-rail-text-strong: #fdf6ec;
    --t-rail-dim:         #7d6a52;
    --t-rail-border:      rgba(232,150,90,0.14);
    --t-rail-card-bg:     rgba(42,32,22,0.6);
    --t-page:         #15100c;
    --t-page-glow:    radial-gradient(circle at 12% -5%, rgba(232,67,26,0.14), transparent 42%);
    --t-panel:        #201811;
    --t-panel-hi:     #2a2016;
    --t-input:        #241b13;
    --t-input-hi:     #2f2419;
    --t-card-input:   #2b2117;
    --t-card-head:    #1e160f;
    --t-surface-mid:  #3a2d1f;
    --t-surface-deep: #100c08;
    --t-border-soft:   rgba(232,150,90,0.20);
    --t-border-softer: rgba(232,150,90,0.11);
    --t-border-mid:    rgba(232,150,90,0.32);
    --t-border-strong: rgba(232,120,60,0.50);
    --t-text-primary: #fdf6ec;
    --t-text-body:    #e8dcc9;
    --t-text-muted:   #b9a68c;
    --t-text-label:   #9a866b;
    --t-text-faint:   #7d6a52;
    --t-text-deep:    #5d4d3a;
    --t-shadow-panel:    0 6px 20px rgba(0,0,0,0.5), 0 0 0 1px rgba(232,150,90,0.07);
    --t-shadow-panel-hi: 0 10px 28px rgba(0,0,0,0.55), 0 0 0 1px rgba(232,150,90,0.10);
    --t-grad-sidebar:    linear-gradient(180deg,#1b1410 0%,#120d09 100%);
    --t-grad-panel-head: linear-gradient(180deg,rgba(42,32,22,0.9),rgba(32,24,17,0.65));
    --t-grad-list-head:  linear-gradient(180deg,rgba(30,22,15,0.92),rgba(30,22,15,0.6));
    --t-compose-bg:      rgba(32,24,17,0.96);
  }
  [data-theme="light"][data-brand-theme="bold"] {
    --t-page:         #fbf5ea;
    --t-page-glow:    radial-gradient(circle at 12% -5%, rgba(232,67,26,0.07), transparent 44%);
    --t-panel:        #fffdf8;
    --t-panel-hi:     #fdf8ee;
    --t-input:        #fffdfa;
    --t-input-hi:     #f7efe0;
    --t-card-input:   #fdf6e8;
    --t-card-head:    #f6eddc;
    --t-surface-mid:  #ebdcc4;
    --t-surface-deep: #d8c6a9;
    --t-border-soft:   rgba(120,85,45,0.22);
    --t-border-softer: rgba(120,85,45,0.12);
    --t-border-mid:    rgba(120,85,45,0.34);
    --t-border-strong: rgba(120,85,45,0.52);
    --t-text-primary: #17100a;
    --t-text-body:    #2e2317;
    --t-text-muted:   #5a4732;
    --t-text-label:   #7a6446;
    --t-text-faint:   #9c8768;
    --t-text-deep:    #c0ac8d;
    /* Hard-edged offset shadow reads as print/editorial rather than glass. */
    --t-shadow-panel:    0 2px 0 rgba(120,85,45,0.10), 0 2px 8px rgba(60,40,20,0.08);
    --t-shadow-panel-hi: 0 3px 0 rgba(120,85,45,0.14), 0 6px 16px rgba(60,40,20,0.10);
    --t-grad-sidebar:    linear-gradient(180deg,#241a12 0%,#191108 100%);
    --t-grad-panel-head: linear-gradient(180deg,#fffdf8,#faf3e6);
    --t-grad-list-head:  linear-gradient(180deg,#f6eddc,rgba(246,237,220,0.75));
    --t-compose-bg:      #fdf8ee;
    /* Warm cream rail with dark serif-adjacent text — matches the paper page. */
    --t-rail-bg:          #f6eddc;
    --t-rail-text:        #5a4732;
    --t-rail-text-strong: #17100a;
    --t-rail-dim:         #9c8768;
    --t-rail-border:      rgba(120,85,45,0.22);
    --t-rail-card-bg:     #fffdf8;
  }

  /* ══ TERMINAL — developer, near-black with a green cast, teal, sharp ══ */
  [data-brand-theme="terminal"] {
    --t-accent:        #2dd4bf;
    --t-accent-2:      #22d3ee;
    --t-accent-grad:   linear-gradient(135deg,#14b8a6,#22d3ee);
    --t-accent-glow:   0 3px 14px rgba(45,212,191,0.38);
    --t-accent-soft:   rgba(45,212,191,0.18);
    --t-accent-softer: rgba(45,212,191,0.07);
    --t-radius-sm:     3px;
    --t-radius-md:     5px;
    --t-radius-lg:     8px;
    /* TERMINAL = developer: JetBrains Mono throughout, uppercase tracked
       buttons with a thin ring instead of a glow. Mono runs ~10% wider than
       Inter, so very dense tables may wrap a little more on this theme —
       it is a per-user cosmetic choice and switching back is instant. */
    --t-font:          'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    --t-font-head:     'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    --t-font-btn:      'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    --t-btn-weight:    700;
    --t-btn-transform: uppercase;
    --t-btn-letter:    0.09em;
    --t-btn-shadow:    0 0 0 1px rgba(45,212,191,0.40), 0 0 12px rgba(45,212,191,0.25);
    /* Terminal icons: thin, square caps, faint teal glow — a console readout. */
    --t-ico-sw:    1.5;
    --t-ico-cap:   square;
    --t-ico-glow:  drop-shadow(0 0 2px rgba(45,212,191,0.5));
  }
  [data-theme="dark"][data-brand-theme="terminal"] {
    --t-rail-bg:          var(--t-grad-sidebar);
    --t-rail-text:        #8db3ac;
    --t-rail-text-strong: #e8fffa;
    --t-rail-dim:         #557b74;
    --t-rail-border:      rgba(45,212,191,0.10);
    --t-rail-card-bg:     rgba(45,212,191,0.06);
    --t-page:         #060b0a;
    --t-page-glow:    radial-gradient(circle at 12% -5%, rgba(45,212,191,0.14), transparent 40%), radial-gradient(circle at 88% 8%, rgba(34,211,238,0.08), transparent 38%);
    --t-panel:        #0c1412;
    --t-panel-hi:     #111b19;
    --t-input:        #0f1917;
    --t-input-hi:     #15211f;
    --t-card-input:   #12201d;
    --t-card-head:    #0d1614;
    --t-surface-mid:  #1a2b28;
    --t-surface-deep: #050a09;
    --t-border-soft:   rgba(45,212,191,0.20);
    --t-border-softer: rgba(45,212,191,0.10);
    --t-border-mid:    rgba(45,212,191,0.32);
    --t-border-strong: rgba(45,212,191,0.50);
    --t-text-primary: #e8fffa;
    --t-text-body:    #c2ded7;
    --t-text-muted:   #8db3ac;
    --t-text-label:   #6d938c;
    --t-text-faint:   #557b74;
    --t-text-deep:    #3e5c56;
    --t-shadow-panel:    0 6px 20px rgba(0,0,0,0.55), 0 0 0 1px rgba(45,212,191,0.06), 0 0 14px rgba(45,212,191,0.05);
    --t-shadow-panel-hi: 0 10px 28px rgba(0,0,0,0.6), 0 0 22px rgba(45,212,191,0.14);
    --t-grad-sidebar:    linear-gradient(180deg,#081110 0%,#040908 100%);
    --t-grad-panel-head: linear-gradient(180deg,rgba(17,27,25,0.9),rgba(12,20,18,0.65));
    --t-grad-list-head:  linear-gradient(180deg,rgba(13,22,20,0.92),rgba(13,22,20,0.6));
    --t-compose-bg:      rgba(12,20,18,0.96);
  }
  [data-theme="light"][data-brand-theme="terminal"] {
    /* Deeper teal for text and links on white — the bright console teal of dark
       mode fell below readable contrast here (2026-10-23). */
    --t-accent:       #0f9488;
    --t-page:         #eef4f2;
    --t-page-glow:    radial-gradient(circle at 12% -5%, rgba(20,184,166,0.07), transparent 44%);
    --t-panel:        #ffffff;
    --t-panel-hi:     #f7fbfa;
    --t-input:        #ffffff;
    --t-input-hi:     #e9f2f0;
    --t-card-input:   #f2f9f7;
    --t-card-head:    #e9f2f0;
    --t-surface-mid:  #d6e6e2;
    --t-surface-deep: #bcd4cf;
    --t-border-soft:   rgba(15,118,110,0.22);
    --t-border-softer: rgba(15,118,110,0.12);
    --t-border-mid:    rgba(15,118,110,0.32);
    --t-border-strong: rgba(15,118,110,0.48);
    --t-text-primary: #06231f;
    --t-text-body:    #14403a;
    --t-text-muted:   #3d6b64;
    --t-text-label:   #55847c;
    --t-text-faint:   #7ba39c;
    --t-text-deep:    #a8c6c0;
    --t-shadow-panel:    0 1px 3px rgba(6,35,31,0.09), 0 1px 2px rgba(6,35,31,0.05);
    --t-shadow-panel-hi: 0 4px 12px rgba(6,35,31,0.11), 0 2px 4px rgba(6,35,31,0.06);
    --t-grad-sidebar:    linear-gradient(180deg,#0c1a18 0%,#061110 100%);
    --t-grad-panel-head: linear-gradient(180deg,#ffffff,#f4faf8);
    --t-grad-list-head:  linear-gradient(180deg,#e9f2f0,rgba(233,242,240,0.75));
    --t-compose-bg:      #f4faf8;
    /* Light console rail — white with teal-green text. */
    --t-rail-bg:          #ffffff;
    --t-rail-text:        #3d6b64;
    --t-rail-text-strong: #06231f;
    --t-rail-dim:         #7ba39c;
    --t-rail-border:      rgba(15,118,110,0.16);
    --t-rail-card-bg:     #f2f9f7;
  }

  /* ══ GLOSSY — polished glass, aqua highlights, soft big corners (2026-10-23) ══
     Panels stay OPAQUE on purpose: dropdowns, menus and modals sit on --t-panel all
     over the app, and a see-through panel would show the page through them. The
     "glass" comes from the --t-gloss highlight, the shine on buttons and the glow. */
  [data-brand-theme="glossy"] {
    --t-accent:        #1a8cff;
    --t-accent-2:      #00c2ff;
    --t-accent-grad:   linear-gradient(180deg,rgba(255,255,255,0.42) 0%,rgba(255,255,255,0.10) 48%,rgba(255,255,255,0) 52%), linear-gradient(135deg,#1a8cff,#00b8f0);
    --t-accent-glow:   0 6px 18px rgba(26,140,255,0.35), inset 0 1px 0 rgba(255,255,255,0.6);
    --t-accent-soft:   rgba(26,140,255,0.16);
    --t-accent-softer: rgba(26,140,255,0.06);
    --t-radius-sm:     10px;
    --t-radius-md:     14px;
    --t-radius-lg:     20px;
    /* GLOSSY = polished and friendly: Plus Jakarta Sans throughout, sentence-case
       buttons with a glassy top shine. */
    --t-font:          'Plus Jakarta Sans', 'Inter', system-ui, -apple-system, sans-serif;
    --t-font-head:     'Plus Jakarta Sans', 'Inter', system-ui, -apple-system, sans-serif;
    --t-font-btn:      'Plus Jakarta Sans', 'Inter', system-ui, -apple-system, sans-serif;
    --t-btn-weight:    700;
    --t-btn-transform: none;
    --t-btn-letter:    0.01em;
    --t-btn-shadow:    var(--t-accent-glow);
    --t-ico-sw:    1.75;
    --t-ico-cap:   round;
    --t-ico-glow:  none;
    --t-backdrop:  blur(18px) saturate(160%);
  }
  [data-theme="dark"][data-brand-theme="glossy"] {
    --t-page:         #0a1222;
    --t-page-glow:    radial-gradient(circle at 15% -10%, rgba(26,140,255,0.28), transparent 45%), radial-gradient(circle at 90% 0%, rgba(0,194,255,0.16), transparent 40%);
    --t-panel:        #131e33;
    --t-panel-hi:     #1b2944;
    --t-input:        #111c31;
    --t-input-hi:     #1a2946;
    --t-card-input:   #17243d;
    --t-card-head:    #16233c;
    --t-surface-mid:  #24365a;
    --t-surface-deep: #08101e;
    --t-border-soft:   rgba(140,190,255,0.20);
    --t-border-softer: rgba(140,190,255,0.11);
    --t-border-mid:    rgba(140,190,255,0.30);
    --t-border-strong: rgba(140,190,255,0.48);
    --t-text-primary: #f2f7ff;
    --t-text-body:    #d2def3;
    --t-text-muted:   #9fb3d4;
    --t-text-label:   #8a9fc2;
    --t-text-faint:   #6f84a8;
    --t-text-deep:    #4b5f82;
    --t-shadow-panel:    0 12px 34px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.12);
    --t-shadow-panel-hi: 0 16px 40px rgba(0,0,0,0.5), 0 0 22px rgba(26,140,255,0.14), inset 0 1px 0 rgba(255,255,255,0.14);
    --t-grad-sidebar:    linear-gradient(180deg,#101a2e 0%,#0a1222 100%);
    --t-grad-panel-head: linear-gradient(180deg,#1d2c48,#16233c);
    --t-grad-list-head:  linear-gradient(180deg,#1a2843,rgba(20,31,54,0.85));
    --t-compose-bg:      rgba(20,32,56,0.96);
    --t-gloss:           linear-gradient(180deg,rgba(255,255,255,0.09),rgba(255,255,255,0) 42%);
    --t-rail-bg:          var(--t-grad-sidebar);
    --t-rail-text:        #a9bcdc;
    --t-rail-text-strong: #f2f7ff;
    --t-rail-dim:         #6f84a8;
    --t-rail-border:      rgba(140,190,255,0.12);
    --t-rail-card-bg:     rgba(255,255,255,0.05);
  }
  [data-theme="light"][data-brand-theme="glossy"] {
    --t-accent:       #0b74e5;
    --t-page:         #e9f1fb;
    --t-page-glow:    radial-gradient(circle at 15% -10%, rgba(26,140,255,0.22), transparent 45%), radial-gradient(circle at 90% 0%, rgba(0,194,255,0.18), transparent 40%), radial-gradient(circle at 50% 110%, rgba(140,120,255,0.12), transparent 45%);
    --t-panel:        #fbfdff;
    --t-panel-hi:     #ffffff;
    --t-input:        #ffffff;
    --t-input-hi:     #f3f8ff;
    --t-card-input:   #f2f8ff;
    --t-card-head:    #f5f9ff;
    --t-surface-mid:  #dbe7f6;
    --t-surface-deep: #c9d9ee;
    --t-border-soft:   rgba(26,90,170,0.16);
    --t-border-softer: rgba(26,90,170,0.09);
    --t-border-mid:    rgba(26,90,170,0.26);
    --t-border-strong: rgba(26,90,170,0.42);
    --t-text-primary: #0a1a33;
    --t-text-body:    #1c2f4d;
    --t-text-muted:   #43577a;
    --t-text-label:   #52678b;
    --t-text-faint:   #62769a;
    --t-text-deep:    #a9b9d2;
    --t-shadow-panel:    0 10px 30px rgba(20,60,120,0.12), 0 2px 6px rgba(20,60,120,0.06), inset 0 1px 0 rgba(255,255,255,0.95);
    --t-shadow-panel-hi: 0 14px 36px rgba(20,60,120,0.16), 0 3px 8px rgba(20,60,120,0.08), inset 0 1px 0 rgba(255,255,255,0.95);
    --t-grad-sidebar:    linear-gradient(180deg,#ffffff 0%,#eef5ff 100%);
    --t-grad-panel-head: linear-gradient(180deg,#ffffff,#f3f8ff);
    --t-grad-list-head:  linear-gradient(180deg,#f3f8ff,rgba(243,248,255,0.8));
    --t-compose-bg:      #ffffff;
    --t-note-bg:         rgba(255,196,0,0.12);
    --t-note-bdr:        rgba(196,128,0,0.45);
    --t-gloss:           linear-gradient(180deg,rgba(255,255,255,0.65),rgba(255,255,255,0) 45%);
    /* Light frosted rail with dark text. */
    --t-rail-bg:          linear-gradient(180deg,#ffffff,#edf4ff);
    --t-rail-text:        #3c5275;
    --t-rail-text-strong: #0a1a33;
    --t-rail-dim:         #62769a;
    --t-rail-border:      rgba(26,90,170,0.12);
    --t-rail-card-bg:     #f3f8ff;
  }

  /* ══ NEON — electric magenta + cyan on violet-black, glowing edges (2026-10-23) ══ */
  [data-brand-theme="neon"] {
    --t-accent:        #ff2bd6;
    --t-accent-2:      #00f0ff;
    --t-accent-grad:   linear-gradient(135deg,#ff2bd6,#8b5cff 55%,#00e5f5);
    --t-accent-glow:   0 0 0 1px rgba(255,43,214,0.6), 0 0 18px rgba(255,43,214,0.55), 0 0 36px rgba(0,240,255,0.25);
    --t-accent-soft:   rgba(255,43,214,0.18);
    --t-accent-softer: rgba(255,43,214,0.07);
    --t-radius-sm:     6px;
    --t-radius-md:     10px;
    --t-radius-lg:     14px;
    /* NEON = arcade signage: Audiowide headings, Exo 2 body, wide-tracked
       uppercase buttons that glow. */
    --t-font:          'Exo 2', 'Inter', system-ui, -apple-system, sans-serif;
    --t-font-head:     'Audiowide', 'Exo 2', system-ui, sans-serif;
    --t-font-btn:      'Exo 2', 'Inter', system-ui, sans-serif;
    --t-btn-weight:    800;
    --t-btn-transform: uppercase;
    --t-btn-letter:    0.12em;
    --t-btn-shadow:    var(--t-accent-glow);
    --t-ico-sw:    1.8;
    --t-ico-cap:   round;
    --t-ico-glow:  drop-shadow(0 0 4px rgba(255,43,214,0.75)) drop-shadow(0 0 8px rgba(0,240,255,0.35));
    --t-head-shadow: 0 0 10px rgba(255,43,214,0.55), 0 0 22px rgba(0,240,255,0.25);
  }
  [data-theme="dark"][data-brand-theme="neon"] {
    --t-page:         #07020f;
    --t-page-glow:    radial-gradient(circle at 10% -5%, rgba(255,43,214,0.22), transparent 40%), radial-gradient(circle at 92% 6%, rgba(0,240,255,0.16), transparent 38%);
    --t-panel:        #10061d;
    --t-panel-hi:     #180a2b;
    --t-input:        #12082a;
    --t-input-hi:     #1c0d38;
    --t-card-input:   #1a0c33;
    --t-card-head:    #13072a;
    --t-surface-mid:  #2a1450;
    --t-surface-deep: #050010;
    --t-border-soft:   rgba(255,43,214,0.30);
    --t-border-softer: rgba(255,43,214,0.16);
    --t-border-mid:    rgba(0,240,255,0.40);
    --t-border-strong: rgba(0,240,255,0.65);
    --t-text-primary: #fff4fd;
    --t-text-body:    #e6d6f5;
    --t-text-muted:   #b39fd0;
    --t-text-label:   #a08cc4;
    --t-text-faint:   #8370a8;
    --t-text-deep:    #4f4170;
    --t-shadow-panel:    0 0 0 1px rgba(255,43,214,0.18), 0 0 18px rgba(255,43,214,0.12), 0 10px 30px rgba(0,0,0,0.6);
    --t-shadow-panel-hi: 0 0 0 1px rgba(0,240,255,0.25), 0 0 24px rgba(255,43,214,0.18), 0 14px 36px rgba(0,0,0,0.65);
    --t-grad-sidebar:    linear-gradient(180deg,#0c0318 0%,#060010 100%);
    --t-grad-panel-head: linear-gradient(180deg,rgba(28,13,56,0.95),rgba(16,6,29,0.7));
    --t-grad-list-head:  linear-gradient(180deg,rgba(19,7,42,0.95),rgba(19,7,42,0.6));
    --t-compose-bg:      rgba(18,6,34,0.96);
    --t-note-bg:         rgba(255,200,0,0.08);
    --t-note-bdr:        rgba(255,200,0,0.45);
    --t-rail-bg:          var(--t-grad-sidebar);
    --t-rail-text:        #c3a9e6;
    --t-rail-text-strong: #fff4fd;
    --t-rail-dim:         #8370a8;
    --t-rail-border:      rgba(255,43,214,0.16);
    --t-rail-card-bg:     rgba(255,43,214,0.06);
  }
  [data-theme="light"][data-brand-theme="neon"] {
    /* Deeper magenta/teal so text and buttons keep their contrast on white. */
    --t-accent:        #c8009b;
    --t-accent-2:      #0096b8;
    --t-accent-grad:   linear-gradient(135deg,#e0119f,#7c3aed 55%,#0aa5c7);
    --t-accent-glow:   0 0 0 1px rgba(214,0,159,0.45), 0 4px 16px rgba(214,0,159,0.30);
    --t-accent-soft:   rgba(214,0,159,0.14);
    --t-accent-softer: rgba(214,0,159,0.05);
    --t-page:         #fbf5ff;
    --t-page-glow:    radial-gradient(circle at 10% -5%, rgba(255,43,214,0.12), transparent 42%), radial-gradient(circle at 92% 6%, rgba(0,200,230,0.12), transparent 40%);
    --t-panel:        #ffffff;
    --t-panel-hi:     #fdf8ff;
    --t-input:        #ffffff;
    --t-input-hi:     #f6ecff;
    --t-card-input:   #faf3ff;
    --t-card-head:    #f6ecff;
    --t-surface-mid:  #ecdcfb;
    --t-surface-deep: #dcc6f2;
    --t-border-soft:   rgba(200,0,170,0.22);
    --t-border-softer: rgba(200,0,170,0.12);
    --t-border-mid:    rgba(0,150,180,0.40);
    --t-border-strong: rgba(0,130,160,0.60);
    --t-text-primary: #1a0630;
    --t-text-body:    #2e1648;
    --t-text-muted:   #5b3f7d;
    --t-text-label:   #6a4e90;
    --t-text-faint:   #7d649f;
    --t-text-deep:    #c4b2dc;
    --t-shadow-panel:    0 0 0 1px rgba(200,0,170,0.10), 0 6px 20px rgba(120,0,140,0.10);
    --t-shadow-panel-hi: 0 0 0 1px rgba(0,150,180,0.18), 0 10px 26px rgba(120,0,140,0.14);
    --t-grad-sidebar:    linear-gradient(180deg,#14051f 0%,#0a0214 100%);
    --t-grad-panel-head: linear-gradient(180deg,#ffffff,#fbf3ff);
    --t-grad-list-head:  linear-gradient(180deg,#f6ecff,rgba(246,236,255,0.75));
    --t-compose-bg:      #fdf8ff;
    --t-note-bg:         rgba(255,190,0,0.10);
    --t-note-bdr:        rgba(180,120,0,0.45);
    --t-head-shadow:     0 0 12px rgba(255,43,214,0.22);
    /* Neon keeps its dark rail in light mode — the sign stays lit. */
    --t-rail-bg:          var(--t-grad-sidebar);
    --t-rail-text:        #c3a9e6;
    --t-rail-text-strong: #fff4fd;
    --t-rail-dim:         #9a86bd;
    --t-rail-border:      rgba(255,43,214,0.16);
    --t-rail-card-bg:     rgba(255,43,214,0.08);
  }
`;

export const KEYFRAMES = `
  @keyframes badge-blue   { 0%,100%{box-shadow:0 0 0 0 rgba(96,165,250,0.30)}  50%{box-shadow:0 0 0 3px rgba(96,165,250,0)} }
  @keyframes badge-amber  { 0%,100%{box-shadow:0 0 0 0 rgba(251,191,36,0.30)}  50%{box-shadow:0 0 0 3px rgba(251,191,36,0)} }
  @keyframes badge-red    { 0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,0.40)}   50%{box-shadow:0 0 0 3px rgba(239,68,68,0)} }
  @keyframes badge-orange { 0%,100%{box-shadow:0 0 0 0 rgba(249,115,22,0.30)}  50%{box-shadow:0 0 0 3px rgba(249,115,22,0)} }
  @keyframes badge-green  { 0%,100%{box-shadow:0 0 0 0 rgba(52,211,153,0.25)}  50%{box-shadow:0 0 0 3px rgba(52,211,153,0)} }
  @keyframes dot-pulse    { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.7;transform:scale(0.85)} }
  @keyframes spin         { to { transform: rotate(360deg); } }
  @keyframes fade-in      { from { opacity:0; transform: translateY(4px); } to { opacity:1; transform: translateY(0); } }
`;

export function ThemeStyles() {
  return <style>{THEME_VARS + KEYFRAMES}</style>;
}
